import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { captureService, type CaptureDetail, type CaptureFrame, type CaptureReplay } from '@/services/captureApi';
import { AuthGuard } from '@/components/Auth/AuthGuard';
import { CaptureNotice, ReplayView, ReportView } from '@/components/captures/CaptureReplay';
import { AddToProjectDialog } from '@/components/captures/AppActions';
import { TranscriptView } from '@/components/captures/CaptureTranscript';
import { isReplayBuilding, isReplayBuilt } from '@/components/captures/replayStatus';
import {
  Loader2, Download, FolderPlus, Camera, ChevronLeft,
  MousePointerClick, Hand, Keyboard, ScrollText, ArrowRightLeft,
  Play, Globe, Sparkles, Check, X, Star, Info, FileText, Code2,
  EyeOff, AlertCircle,
} from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import Logo from '@/components/ui/logo';

export const Route = createFileRoute('/captures_/$captureId')({
  component: () => <AuthGuard><CaptureEditorPage /></AuthGuard>,
});

interface FrameModel {
  index: number;
  screenshot: string | null;
  elements: any[];
  url: string;
  title: string;
  triggerType: string;
  trigger: CaptureFrame['trigger'];
}

interface TransitionModel {
  index: number;
  from: number;
  to: number;
  action: string;
  cursor: { x: number; y: number };
  target: { tag: string; text: string; rect: any; selector?: string; id?: string } | null;
  timeDelta: number;
}

interface FrameState {
  selected: boolean;
  keyframe: boolean;
  skipped: boolean;
  note: string;
}

type PageView = 'replay' | 'steps' | 'transcript' | 'report';

const PAGE_VIEWS: { id: PageView; label: string }[] = [
  { id: 'replay', label: 'Replay' },
  { id: 'steps', label: 'Steps' },
  { id: 'transcript', label: 'Transcript' },
  { id: 'report', label: 'Report' },
];

const REPLAY_POLL_MS = 5000;
// A zip capture's steps are read from the upload within seconds of it finishing (api-server captureSteps.ts).
const STEPS_POLL_MS = 3000;
// Steps not there by then aren't coming from this upload: stop asking, and say so.
const STEPS_WAIT_MS = 10 * 60_000;

function CaptureEditorPage() {
  const { captureId } = Route.useParams();
  const navigate = useNavigate();

  const [capture, setCapture] = useState<CaptureDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMsg, setLoadingMsg] = useState('Loading capture...');

  const [frames, setFrames] = useState<FrameModel[]>([]);
  const [transitions, setTransitions] = useState<TransitionModel[]>([]);
  const [frameStates, setFrameStates] = useState<FrameState[]>([]);

  const [selType, setSelType] = useState<'frame' | 'transition'>('frame');
  const [selIdx, setSelIdx] = useState(0);
  const [tab, setTab] = useState<'inspect' | 'notes' | 'data'>('inspect');
  const [view, setView] = useState<PageView | null>(null);
  // Opened on a built replay: the page opens on Replay. Otherwise it opens on the steps as soon as they exist, and
  // stays there when the replay finishes, so nobody is moved off what they are reading.
  const [openedBuilt, setOpenedBuilt] = useState<boolean | null>(null);
  const [stepsGaveUp, setStepsGaveUp] = useState(false);
  const [replay, setReplay] = useState<CaptureReplay | null>(null);
  const [showRects, setShowRects] = useState(true);
  const [showInt, setShowInt] = useState(true);

  const [hoveredElement, setHoveredElement] = useState<any>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  // Tick state used solely to force renderRects to re-run after the image
  // loads / resizes. Actual dimensions are read from the ref in renderRects —
  // state-synced dims would get stale if onLoad silently misses (e.g. cached
  // image completed before the React listener attached).
  const [, setRenderTick] = useState(0);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useLayoutEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    const tick = () => setRenderTick(t => t + 1);
    // Cached / already-decoded images won't fire load — tick immediately so
    // the overlay doesn't wait for a resize that never comes.
    if (img.complete && img.naturalWidth > 0) tick();
    img.addEventListener('load', tick);
    const ro = new ResizeObserver(tick);
    ro.observe(img);
    return () => {
      img.removeEventListener('load', tick);
      ro.disconnect();
    };
  }, [selType, selIdx, frames.length]);

  // Load capture + elements
  const loadCapture = useCallback(async () => {
    try {
      const data = await captureService.get(captureId);

      if (data.status === 'PROCESSING' && !data.format) {
        setLoadingMsg('Processing frames... retrying...');
        setTimeout(loadCapture, 3000);
        return;
      }

      setCapture(data);
      setOpenedBuilt(prev => prev ?? isReplayBuilt(data.replayStatus));

      // Load elements for each frame
      const frameModels: FrameModel[] = [];
      for (let i = 0; i < data.frames.length; i++) {
        const f = data.frames[i];
        setLoadingMsg(`Loading elements... ${i + 1}/${data.frames.length}`);

        let elements: any[] = [];
        if (f.elementsKey) {
          try { elements = await captureService.getFrameElements(captureId, f.index); } catch {}
        }

        frameModels.push({
          index: f.index,
          screenshot: f.screenshotUrl,
          elements,
          url: data.url,
          title: data.name,
          triggerType: f.trigger?.type || 'auto',
          trigger: f.trigger,
        });
      }

      // Build transitions
      const trans: TransitionModel[] = [];
      for (let i = 1; i < frameModels.length; i++) {
        const trigger = data.frames[i].trigger || {};
        const action = trigger.type || 'auto';
        const cursor = trigger.cursor || { x: 0, y: 0 };
        const prevElements = frameModels[i - 1].elements;

        let target: TransitionModel['target'] = null;
        if (trigger.element?.text && cursor.x > 0) {
          let best = Infinity;
          for (const el of prevElements) {
            if (action === 'click' && !el.interactive) continue;
            if (!el.rect) continue;
            const cx = el.rect.x + (el.rect.w || el.rect.width || 0) / 2;
            const cy = el.rect.y + (el.rect.h || el.rect.height || 0) / 2;
            const d = Math.sqrt((cx - cursor.x) ** 2 + (cy - cursor.y) ** 2);
            if (d < best && d < 120) {
              best = d;
              target = { tag: el.tag, text: (el.text || '').substring(0, 50), rect: el.rect, selector: el.selector, id: el.id };
            }
          }
        }

        trans.push({ index: i - 1, from: i - 1, to: i, action, cursor, target, timeDelta: 1000 });
      }

      const states = data.frames.map(f => ({
        selected: !f.excluded,
        keyframe: false,
        skipped: f.excluded,
        note: f.notes || '',
      }));

      setFrames(frameModels);
      setTransitions(trans);
      setFrameStates(states);
      setLoading(false);
    } catch (e: any) {
      setLoadingMsg('Error: ' + e.message);
    }
  }, [captureId]);

  useEffect(() => {
    setLoading(true);
    setView(null);
    setOpenedBuilt(null);
    setReplay(null);
    loadCapture();
  }, [loadCapture]);

  const hasReplay = !!capture && !!(capture.format || capture.replayStatus);
  const activeView: PageView = view ?? (hasReplay && (openedBuilt || frames.length === 0) ? 'replay' : 'steps');
  const followReplay = hasReplay && capture?.status !== 'FAILED';
  const uploading = capture?.status === 'PROCESSING';
  const waitingForSteps = !!capture?.format && capture.status !== 'FAILED' && frames.length === 0
    && !isReplayBuilt(capture.replayStatus) && capture.replayStatus !== 'failed' && !stepsGaveUp;

  useEffect(() => {
    if (!waitingForSteps) return;
    const started = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - started >= STEPS_WAIT_MS) setStepsGaveUp(true);
      else loadCapture();
    }, STEPS_POLL_MS);
    return () => clearInterval(timer);
  }, [waitingForSteps, loadCapture]);

  useEffect(() => {
    if (!followReplay) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    let waited = false;
    const poll = async () => {
      try {
        const next = await captureService.getReplay(captureId);
        if (cancelled) return;
        setReplay(next);
        if (!next.replayStatus || isReplayBuilding(next.replayStatus)) {
          waited = true;
          timer = setTimeout(poll, REPLAY_POLL_MS);
        } else if (waited) {
          loadCapture();
        }
      } catch {
        if (!cancelled) timer = setTimeout(poll, REPLAY_POLL_MS);
      }
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [captureId, followReplay, loadCapture]);

  // Selection
  const select = useCallback((type: 'frame' | 'transition', idx: number) => {
    setSelType(type);
    setSelIdx(idx);
    setHoveredElement(null);
  }, []);

  // Frame state mutations
  function toggleSkip(i: number) {
    setFrameStates(prev => {
      const next = [...prev];
      next[i] = { ...next[i], skipped: !next[i].skipped, selected: next[i].skipped, keyframe: next[i].skipped ? next[i].keyframe : false };
      return next;
    });
  }

  function toggleKey(i: number) {
    setFrameStates(prev => {
      const next = [...prev];
      next[i] = { ...next[i], keyframe: !next[i].keyframe, selected: true, skipped: false };
      return next;
    });
  }

  function setFrameStatus(i: number, status: 'include' | 'key' | 'skip') {
    setFrameStates(prev => {
      const next = [...prev];
      if (status === 'include') next[i] = { ...next[i], selected: true, skipped: false, keyframe: false };
      if (status === 'key') next[i] = { ...next[i], keyframe: !next[i].keyframe, selected: true, skipped: false };
      if (status === 'skip') next[i] = { ...next[i], skipped: !next[i].skipped, selected: next[i].skipped };
      return next;
    });
  }

  function updateNote(frameIdx: number, note: string) {
    setFrameStates(prev => {
      const next = [...prev];
      next[frameIdx] = { ...next[frameIdx], note };
      return next;
    });
    scheduleSave();
  }

  function scheduleSave() {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      if (!capture) return;
      const changed = frameStates.map((fs, i) => ({
        index: i, notes: fs.note || undefined, excluded: fs.skipped,
      })).filter((f, i) => {
        const orig = capture.frames[i];
        return f.notes !== (orig.notes || null) || f.excluded !== (orig.excluded || false);
      });
      if (changed.length) {
        try { await captureService.update(captureId, { frames: changed }); } catch {}
      }
    }, 1500);
  }

  function skipAllAuto() {
    setFrameStates(prev => prev.map((fs, i) => {
      if ((frames[i]?.triggerType === 'mutation' || frames[i]?.triggerType === 'auto') && !fs.keyframe) {
        return { ...fs, skipped: true, selected: false };
      }
      return fs;
    }));
  }

  // Keyboard
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (activeView !== 'steps') return;
      if ((e.target as HTMLElement).tagName === 'TEXTAREA' || (e.target as HTMLElement).tagName === 'INPUT') return;
      const total = frames.length + transitions.length;
      let linear = selType === 'frame' ? selIdx * 2 : selIdx * 2 + 1;

      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        linear = Math.min(linear + 1, total - 1);
        if (linear % 2 === 0) select('frame', linear / 2);
        else select('transition', (linear - 1) / 2);
      }
      if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        linear = Math.max(linear - 1, 0);
        if (linear % 2 === 0) select('frame', linear / 2);
        else select('transition', (linear - 1) / 2);
      }
      if (e.key === 'x' && selType === 'frame') toggleSkip(selIdx);
      if (e.key === 's' && selType === 'frame') toggleKey(selIdx);
      if (e.key === 'n') setTab('notes');
      if (e.key === 'i') setTab('inspect');
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [activeView, select, selType, selIdx, frames.length, transitions.length]);

  // Export brief
  function exportDemoBrief() {
    if (!capture) return;
    const selectedIndices = frameStates.map((fs, i) => fs.selected ? i : -1).filter(i => i >= 0);
    const viewport = capture.metadata?.viewport || { width: 1920, height: 963 };

    const steps = selectedIndices.map((fi, si) => {
      const f = frames[fi];
      const fs = frameStates[fi];
      const nextFi = si < selectedIndices.length - 1 ? selectedIndices[si + 1] : null;
      let outgoing: TransitionModel | null = null;
      if (nextFi !== null) outgoing = transitions.find(t => t.from === fi) || null;

      const headings = f.elements.filter(e => ['h1', 'h2', 'h3'].includes(e.tag) && e.text?.length > 2).map(e => e.text.substring(0, 60));
      const buttons = f.elements.filter(e => (e.tag === 'button' || e.role === 'button') && e.text?.length > 1 && e.text.length < 30).map(e => e.text);

      return {
        step: si + 1,
        frame: fi,
        keyframe: fs.keyframe || undefined,
        note: fs.note || undefined,
        screenshot: f.screenshot,
        scene: {
          page: f.title, url: f.url,
          headings: headings.length ? headings : undefined,
          buttons: buttons.length ? buttons.slice(0, 8) : undefined,
        },
        then: outgoing ? {
          action: outgoing.action,
          target: outgoing.target ? `<${outgoing.target.tag}> "${outgoing.target.text}"` : undefined,
          cursor: outgoing.cursor.x > 0 ? outgoing.cursor : undefined,
        } : undefined,
      };
    });

    const brief = {
      type: 'demo-brief', version: '2.0', captureId,
      recording: { title: capture.name, url: capture.url, viewport: `${viewport.width}x${viewport.height}` },
      steps: JSON.parse(JSON.stringify(steps)),
      summary: {
        steps: steps.length, totalFrames: capture.frameCount,
        excluded: frameStates.filter(fs => fs.skipped).length,
        annotated: frameStates.filter(fs => fs.note).length,
      },
    };

    const blob = new Blob([JSON.stringify(brief, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `demo-brief-${captureId.substring(0, 8)}.json`;
    a.click();
  }

  function openImportModal() {
    if (!capture) return;
    if (!frameStates.some(fs => fs.selected)) {
      alert('Select at least one frame before adding to a project.');
      return;
    }
    setImportModalOpen(true);
  }

  // Render rects
  function renderRects() {
    const img = imgRef.current;
    if (!img || !img.naturalWidth || !img.clientWidth) return null;

    // Screenshots are captured at the full viewport width (CSS pixels).
    // Metadata *should* carry the right viewport, but on pages that load
    // sub-frames before the top frame's Meta event (Notion's 1×1 tracker,
    // Calendar's 300×150 contacts hovercard, etc.) the wrong dimensions get
    // stored. Only trust the metadata when it matches the screenshot size
    // at DPR=1 or DPR=2; otherwise fall back to the screenshot dimensions.
    const metaW = capture?.metadata?.viewport?.width;
    const nW = img.naturalWidth;
    const metaLooksRight = !!metaW && (Math.abs(metaW - nW) < nW * 0.2 || Math.abs(metaW - nW / 2) < nW * 0.1);
    const viewportW = metaLooksRight ? metaW! : (nW > 3000 ? nW / 2 : nW);
    const s = img.clientWidth / viewportW;

    let elements: any[] = [];
    let targetId: string | null = null;
    let cursor: { x: number; y: number } | null = null;
    let cursorAction: string | null = null;

    if (selType === 'frame') {
      elements = frames[selIdx]?.elements || [];
    } else {
      const t = transitions[selIdx];
      if (t) {
        elements = frames[t.from]?.elements || [];
        cursor = t.cursor;
        cursorAction = t.action;
        targetId = t.target?.id || null;
      }
    }

    const rects: JSX.Element[] = [];
    const metaH = capture?.metadata?.viewport?.height;
    const nH = img.naturalHeight;
    const metaHLooksRight = !!metaH && (Math.abs(metaH - nH) < nH * 0.2 || Math.abs(metaH - nH / 2) < nH * 0.1);
    const viewportH = metaHLooksRight ? metaH! : (nH > 1500 ? nH / 2 : nH);
    const vpArea = viewportW * viewportH;
    if (showRects || showInt) {
      elements.forEach((el, i) => {
        if (!showRects && !(showInt && el.interactive)) return;
        const r = el.rect;
        const w = r?.w || r?.width || 0;
        const h = r?.h || r?.height || 0;
        if (!r || w < 4 || h < 4) return;
        // Skip layout wrappers. Two rules, because captured `text` is
        // `textContent` which bubbles up from descendants — every ancestor of
        // a text block inherits its text. So "has text" alone can't tell us
        // whether an element is meaningful or just a wrapper.
        //  - Big rects (>40% viewport area): require a real semantic signal.
        //  - Small rects: semantic signal OR short direct-ish text is fine.
        const hasSemantic = el.interactive || el.mediaType || el.role || el.ariaLabel;
        const hasShortText = el.text && el.text.length > 0 && el.text.length < 120;
        const isBig = (w * h) / vpArea > 0.4;
        const meaningful = isBig ? hasSemantic : (hasSemantic || hasShortText);
        if (!meaningful) return;
        const isTarget = el.id === targetId;

        rects.push(
          <div
            key={i}
            className={cn(
              'absolute border pointer-events-auto cursor-crosshair transition-colors',
              isTarget
                ? 'border-2 border-amber-500 bg-amber-500/10 z-20 shadow-[0_0_10px_rgba(245,158,11,0.25)]'
                : el.interactive
                ? 'border-emerald-500/40 hover:bg-emerald-500/10 hover:border-emerald-500/70'
                : 'border-primary/35 hover:bg-primary/10 hover:border-primary/70',
            )}
            style={{ left: r.x * s, top: r.y * s, width: w * s, height: h * s }}
            onMouseEnter={() => setHoveredElement(el)}
          />
        );
      });
    }

    if (cursor && cursor.x > 0 && (cursorAction === 'click' || cursorAction === 'hover')) {
      rects.push(
        <div key="cursor" className="absolute pointer-events-none z-30" style={{ left: cursor.x * s, top: cursor.y * s, transform: 'translate(-50%,-50%)' }}>
          <div className={`w-3.5 h-3.5 rounded-full ${cursorAction === 'hover' ? 'bg-primary/90 shadow-[0_0_0_4px_rgba(96,165,250,0.2)]' : 'bg-amber-500/90 shadow-[0_0_0_4px_rgba(245,158,11,0.2)]'}`} />
          <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full border-2 ${cursorAction === 'hover' ? 'border-primary/40' : 'border-amber-500/40'} animate-ping`} />
          <div className="absolute top-5 left-1/2 -translate-x-1/2 bg-black/85 text-amber-500 text-[9px] font-semibold px-1.5 py-0.5 rounded font-mono whitespace-nowrap">
            {cursorAction} ({cursor.x}, {cursor.y})
          </div>
        </div>
      );
    }

    return rects;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center flex-col gap-4">
        <div className="relative">
          <div className="absolute inset-0 bg-primary/20 blur-2xl rounded-full" />
          <Loader2 className="relative h-6 w-6 animate-spin text-primary" />
        </div>
        <p className="text-sm text-muted-foreground">{loadingMsg}</p>
      </div>
    );
  }

  if (!capture) return null;

  const selectedFrame = selType === 'frame' ? frames[selIdx] : transitions[selIdx] ? frames[transitions[selIdx].from] : null;
  const selectedCount = frameStates.filter(fs => fs.selected).length;
  const keptSteps = frames.filter((_, i) => frameStates[i]?.selected).map(f => f.index);
  const appState = replay && !replay.hosted
    ? 'unavailable' as const
    : isReplayBuilt(replay?.replayStatus ?? null) && replay?.viewerUrl
      ? 'built' as const
      : isReplayBuilding(replay?.replayStatus ?? capture.replayStatus) ? 'building' as const : null;

  const hostname = (() => {
    try { return new URL(selectedFrame?.url || capture.url || '').hostname; } catch { return ''; }
  })();
  const captureHost = (() => {
    try { return new URL(capture.url).hostname; } catch { return capture.url || 'this app'; }
  })();

  const failed = capture.status === 'FAILED';
  const stepsPending = hasReplay && frames.length === 0;
  const body: PageView | 'failed' | 'no-steps' = failed
    ? 'failed'
    : activeView === 'steps' && stepsPending ? 'no-steps' : activeView;

  return (
    <div className="h-screen bg-background text-foreground flex flex-col overflow-hidden">
      {/* Topbar — Logo leads (clickable → home), discrete back chip, then title */}
      <header className="px-3 h-12 flex items-center gap-2 shrink-0 studio-topbar">
        {/* Logo → home */}
        <button
          onClick={() => navigate({ to: '/', search: { slide: undefined } })}
          className="flex items-center rounded-lg -mx-1 px-1 hover:bg-black/5 dark:hover:bg-white/5 transition"
          title="Go to home"
        >
          <Logo variant="modern" className="h-7" />
        </button>

        {/* Subtle back-to-parent chip (Gmail / Linear pattern) */}
        <button
          onClick={() => navigate({ to: '/captures' })}
          className="group inline-flex items-center gap-0.5 h-7 rounded-md pl-0.5 pr-2 text-xs font-medium studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5 transition ml-1"
          title="Back to Captures"
        >
          <ChevronLeft className="h-3.5 w-3.5 -mr-0.5 opacity-60 group-hover:opacity-100 transition" />
          Captures
        </button>

        <div className="h-5 w-px bg-black/10 dark:bg-white/10" />

        {/* Capture title + live metadata */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <h1 className="text-sm font-semibold truncate studio-text-primary">{capture.name}</h1>
          <span className="hidden md:inline-flex items-center gap-1 text-[11px] studio-text-secondary tabular-nums">
            <span>{frames.length} frames</span>
            <span className="opacity-40">·</span>
            <span>{transitions.length} transitions</span>
            {capture.metadata?.viewport && (
              <>
                <span className="opacity-40">·</span>
                <span>{capture.metadata.viewport.width}×{capture.metadata.viewport.height}</span>
              </>
            )}
          </span>
        </div>

        {hasReplay && !failed && (
          <div role="tablist" aria-label="Capture views" className="flex items-center gap-0.5 rounded-lg bg-muted p-0.5 shrink-0">
            {PAGE_VIEWS.filter(({ id }) => id !== 'transcript' || capture.audio).map(({ id, label }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={activeView === id}
                onClick={() => setView(id)}
                className={cn(
                  'rounded-md px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  activeView === id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Right-side actions */}
        <div className={cn('flex items-center gap-1.5 shrink-0', body !== 'steps' && 'hidden')}>
          <button
            onClick={skipAllAuto}
            className="h-8 inline-flex items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5 transition"
            title="Deselect mutation / scroll frames"
          >
            <EyeOff className="h-3.5 w-3.5" />
            <span className="hidden lg:inline">Skip auto</span>
          </button>
          <button
            onClick={exportDemoBrief}
            className="h-8 inline-flex items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5 transition"
            title="Export brief as JSON"
          >
            <Download className="h-3.5 w-3.5" />
            <span className="hidden lg:inline">Export</span>
          </button>
          <div className="h-5 w-px bg-black/10 dark:bg-white/10 mx-1" />
          <button
            onClick={openImportModal}
            className={cn(
              'h-8 inline-flex items-center gap-1.5 rounded-lg px-3 text-xs font-semibold transition',
              'bg-primary text-primary-foreground hover:bg-primary/90',
              'shadow-[0_0_0_1px_hsl(var(--primary)/0.25),0_4px_14px_hsl(var(--primary)/0.25)]',
              'disabled:opacity-60 disabled:cursor-not-allowed',
            )}
          >
            <FolderPlus className="h-3.5 w-3.5" />
            Add to Project
          </button>
        </div>
      </header>

      {body === 'failed' && (
        <CaptureNotice icon={AlertCircle} title="This capture could not be processed.">
          {capture.metadata?.processingError}
        </CaptureNotice>
      )}
      {body === 'replay' && (
        <ReplayView
          captureId={captureId}
          captureName={capture.name}
          replay={replay}
          host={captureHost}
          uploading={uploading}
          stepIndices={keptSteps}
          onOpenReport={() => setView('report')}
        />
      )}
      {body === 'report' && (
        <ReportView captureId={captureId} buildId={replay?.buildId ?? capture.replayBuildId} />
      )}
      {body === 'transcript' && capture.audio && (
        <TranscriptView
          captureId={captureId}
          tracks={capture.audio.tracks}
          steps={frames.map(f => ({ stepId: f.trigger?.stepId ?? '', title: f.trigger?.title || f.title, screenshot: f.screenshot }))}
        />
      )}
      {body === 'no-steps' && (
        waitingForSteps
          ? <CaptureNotice icon={Loader2} spin title="Reading the steps from the upload…" />
          : <CaptureNotice icon={Camera} title={stepsGaveUp ? "The steps couldn't be read from this upload." : 'This capture has no steps.'} />
      )}

      <div className={cn('flex flex-1 overflow-hidden', body !== 'steps' && 'hidden')}>
        {/* Storyboard sidebar */}
        <aside className="w-64 border-r border-border flex flex-col shrink-0 bg-card/30">
          <div className="px-3 h-10 border-b border-border flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Storyboard
            </span>
            <span className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold',
              selectedCount > 0 ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
            )}>
              <Check className="h-3 w-3" />
              {selectedCount} selected
            </span>
          </div>
          <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
            {frames.map((f, i) => (
              <div key={`f-${i}`}>
                {/* Frame thumbnail */}
                <div
                  className={cn(
                    'group/frame relative rounded-lg overflow-hidden cursor-pointer border-2 transition-all',
                    selType === 'frame' && selIdx === i
                      ? 'border-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.1)]'
                      : frameStates[i]?.keyframe
                      ? 'border-emerald-500/60'
                      : 'border-transparent hover:border-border',
                    frameStates[i]?.skipped && 'opacity-40',
                  )}
                  onClick={() => select('frame', i)}
                >
                  {f.screenshot ? (
                    <img src={f.screenshot} className="w-full block" loading="lazy" />
                  ) : (
                    <div className="w-full aspect-video bg-muted" />
                  )}
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/85 via-black/50 to-transparent flex items-center gap-1.5">
                    <span className="text-[10px] text-white/80 font-mono font-semibold tabular-nums">#{String(i).padStart(2, '0')}</span>
                    <TriggerPill type={f.triggerType} />
                  </div>
                  {frameStates[i]?.note && (
                    <div
                      className="absolute top-1.5 left-1.5 h-2 w-2 rounded-full bg-amber-400 ring-2 ring-black/60"
                      title="Has note"
                    />
                  )}
                  <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover/frame:opacity-100 transition-opacity">
                    <button
                      className={cn(
                        'h-5 w-5 rounded text-[10px] flex items-center justify-center bg-black/70 backdrop-blur-sm transition',
                        frameStates[i]?.skipped ? 'text-red-400' : 'text-white/80 hover:text-red-400',
                      )}
                      onClick={(e) => { e.stopPropagation(); toggleSkip(i); }}
                      title="Skip frame (x)"
                    >
                      <X className="h-3 w-3" strokeWidth={2.5} />
                    </button>
                    <button
                      className={cn(
                        'h-5 w-5 rounded text-[10px] flex items-center justify-center bg-black/70 backdrop-blur-sm transition',
                        frameStates[i]?.keyframe ? 'text-emerald-400' : 'text-white/80 hover:text-emerald-400',
                      )}
                      onClick={(e) => { e.stopPropagation(); toggleKey(i); }}
                      title="Mark as keyframe (s)"
                    >
                      <Star className="h-3 w-3" strokeWidth={2.2} fill={frameStates[i]?.keyframe ? 'currentColor' : 'none'} />
                    </button>
                  </div>
                </div>

                {/* Transition arrow */}
                {i < transitions.length && (
                  <div
                    className={cn(
                      'flex items-center gap-1.5 px-2 py-1.5 rounded-md cursor-pointer transition',
                      selType === 'transition' && selIdx === i
                        ? 'bg-primary/10 ring-1 ring-primary/30'
                        : 'hover:bg-accent',
                    )}
                    onClick={() => select('transition', i)}
                  >
                    <div className="h-px flex-1 bg-border" />
                    <TriggerPill type={transitions[i].action} small />
                    {transitions[i].target?.text && (
                      <span className="text-[10px] text-muted-foreground truncate max-w-[80px]">
                        {transitions[i].target!.text}
                      </span>
                    )}
                    <div className="h-px flex-1 bg-border" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>

        {/* Viewport */}
        <main className="flex-1 flex flex-col overflow-hidden bg-muted/30">
          <div className="px-4 h-10 border-b border-border flex items-center gap-3 text-xs shrink-0 bg-card/50">
            <div className="flex items-center gap-2 text-muted-foreground font-medium mr-auto">
              {selType === 'frame' ? (
                <>
                  <span className="text-[10px] font-mono font-bold text-foreground tabular-nums">#{String(selIdx).padStart(2, '0')}</span>
                  <span className="h-3 w-px bg-border" />
                  <Globe className="h-3.5 w-3.5" />
                  <span className="truncate max-w-md">{hostname || selectedFrame?.url}</span>
                </>
              ) : (
                <>
                  <TriggerPill type={transitions[selIdx]?.action} />
                  <span className="truncate">
                    Frame {transitions[selIdx]?.from} → {transitions[selIdx]?.to}
                  </span>
                </>
              )}
            </div>
            <label className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground cursor-pointer transition">
              <input type="checkbox" checked={showRects} onChange={e => setShowRects(e.target.checked)} className="accent-primary h-3.5 w-3.5" />
              <span className="text-[11px] font-medium">All rects</span>
            </label>
            <label className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground cursor-pointer transition">
              <input type="checkbox" checked={showInt} onChange={e => setShowInt(e.target.checked)} className="accent-primary h-3.5 w-3.5" />
              <span className="text-[11px] font-medium">Interactive</span>
            </label>
          </div>
          <div className="flex-1 overflow-auto p-6 flex items-start justify-center">
            {/* Browser chrome */}
            <div className="rounded-xl overflow-hidden bg-card ring-1 ring-border shadow-[0_20px_48px_-20px_rgba(0,0,0,0.25)] max-w-full">
              <div className="h-8 flex items-center gap-1.5 px-3 bg-muted/60 border-b border-border">
                <span className="h-2.5 w-2.5 rounded-full bg-red-400/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
                <div className="mx-auto max-w-sm truncate text-[11px] text-muted-foreground font-mono px-3 py-0.5 rounded bg-background/80 border border-border">
                  {selectedFrame?.url || capture.url}
                </div>
                <span className="h-2.5 w-8" />
              </div>
              <div className="relative">
                {selectedFrame?.screenshot ? (
                  <img
                    ref={imgRef}
                    src={selectedFrame.screenshot}
                    className="block max-w-full h-auto"
                    onLoad={() => setRenderTick(t => t + 1)}
                  />
                ) : (
                  <div className="w-[960px] h-[540px] bg-muted flex items-center justify-center text-muted-foreground">
                    No screenshot
                  </div>
                )}
                {renderRects()}
              </div>
            </div>
          </div>
          <div className="px-4 h-9 border-t border-border flex items-center gap-6 text-[11px] text-muted-foreground shrink-0 bg-card/50">
            <span className="flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" />
              <span className="font-semibold text-foreground tabular-nums">{selectedFrame?.elements.length || 0}</span>
              <span>elements</span>
            </span>
            <span className="flex items-center gap-1.5">
              <MousePointerClick className="h-3 w-3" />
              <span className="font-semibold text-foreground tabular-nums">{selectedFrame?.elements.filter(e => e.interactive).length || 0}</span>
              <span>interactive</span>
            </span>
            <span className="ml-auto text-[10px] font-mono">
              {capture.metadata?.viewport?.width}×{capture.metadata?.viewport?.height}
            </span>
          </div>
        </main>

        {/* Right panel */}
        <aside className="w-80 border-l border-border flex flex-col shrink-0 overflow-hidden bg-card/30">
          <div className="flex border-b border-border shrink-0 h-10">
            {([
              { id: 'inspect' as const, label: 'Inspect', Icon: Info },
              { id: 'notes' as const, label: 'Notes', Icon: FileText },
              { id: 'data' as const, label: 'Data', Icon: Code2 },
            ]).map(({ id, label, Icon }) => (
              <button
                key={id}
                className={cn(
                  'flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold border-b-2 transition-colors',
                  tab === id ? 'text-foreground border-primary' : 'text-muted-foreground border-transparent hover:text-foreground',
                )}
                onClick={() => setTab(id)}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto">
            {tab === 'inspect' && (
              hoveredElement ? <ElementInspector el={hoveredElement} /> : (
                selType === 'transition' ? <TransitionInspector t={transitions[selIdx]} /> : <FrameInspector f={frames[selIdx]} />
              )
            )}
            {tab === 'notes' && (
              <NotesPanel
                frameIdx={selType === 'frame' ? selIdx : transitions[selIdx]?.to ?? 0}
                frameStates={frameStates}
                onUpdateNote={updateNote}
                onSetStatus={setFrameStatus}
              />
            )}
            {tab === 'data' && (
              <div className="p-4">
                <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Raw</h3>
                <pre className="text-[10px] text-muted-foreground whitespace-pre-wrap break-all max-h-[600px] overflow-auto font-mono bg-muted/50 p-3 rounded-lg border border-border">
                  {JSON.stringify(
                    selType === 'transition' ? transitions[selIdx] : { index: frames[selIdx]?.index, url: frames[selIdx]?.url, elements: frames[selIdx]?.elements.length },
                    null, 2
                  )}
                </pre>
              </div>
            )}
          </div>
        </aside>
      </div>

      {importModalOpen && (
        <AddToProjectDialog
          captureId={captureId}
          captureName={capture.name}
          stepIndices={keptSteps}
          app={appState}
          needsFix={replay?.replayStatus === 'needs_fix' ? replay.replaySummary?.reasons ?? [] : null}
          onClose={() => setImportModalOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Trigger Pill ─────────────────────────────────────────────────────────

function TriggerPill({ type, small = false }: { type?: string; small?: boolean }) {
  const cfg: Record<string, { icon: typeof MousePointerClick; label: string; color: string }> = {
    click: { icon: MousePointerClick, label: 'click', color: 'text-primary bg-primary/15' },
    hover: { icon: Hand, label: 'hover', color: 'text-orange-400 bg-orange-400/15' },
    input: { icon: Keyboard, label: 'input', color: 'text-primary bg-primary/15' },
    scroll: { icon: ScrollText, label: 'scroll', color: 'text-primary bg-primary/15' },
    mutation: { icon: ArrowRightLeft, label: 'mutation', color: 'text-red-400 bg-red-400/15' },
    load: { icon: Play, label: 'load', color: 'text-emerald-400 bg-emerald-400/15' },
    navigation: { icon: Globe, label: 'nav', color: 'text-lime-400 bg-lime-400/15' },
    auto: { icon: Sparkles, label: 'auto', color: 'text-muted-foreground bg-muted' },
    screenshot: { icon: Camera, label: 'shot', color: 'text-muted-foreground bg-muted' },
  };
  const c = cfg[type || 'auto'] || cfg.auto;
  const Icon = c.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded font-semibold uppercase tracking-wider',
        small ? 'px-1.5 py-0.5 text-[9px]' : 'px-1.5 py-0.5 text-[9px]',
        c.color,
      )}
    >
      <Icon className={small ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
      {c.label}
    </span>
  );
}

// ─── Inspector Support ───────────────────────────────────────────────────

function PropRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-2 py-1 text-xs">
      <span className="text-muted-foreground font-medium shrink-0">{k}</span>
      <span className="text-foreground font-mono text-[11px] max-w-[180px] truncate" title={v}>{v}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="px-4 py-3 border-b border-border">
      <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">{title}</h3>
      {children}
    </div>
  );
}

function ElementInspector({ el }: { el: any }) {
  const s = el.styles || {};
  return (
    <>
      <Section title="Element">
        <PropRow k="tag" v={`<${el.tag}>`} />
        <PropRow k="id" v={el.id || ''} />
        <PropRow k="role" v={el.role || '—'} />
        <PropRow k="selector" v={el.selector || ''} />
      </Section>
      <Section title="Position">
        <PropRow k="rect" v={`${el.rect?.x},${el.rect?.y} ${el.rect?.w || el.rect?.width}×${el.rect?.h || el.rect?.height}`} />
        <PropRow k="depth" v={String(el.depth || 0)} />
      </Section>
      <Section title="Content">
        <PropRow k="text" v={(el.text || '—').substring(0, 60)} />
        {el.ariaLabel && <PropRow k="aria-label" v={el.ariaLabel} />}
      </Section>
      <Section title="Visual">
        {s.bgColor && <PropRow k="bg" v={s.bgColor} />}
        {s.color && <PropRow k="color" v={s.color} />}
        {s.fontSize && <PropRow k="font" v={`${s.fontFamily || ''} ${s.fontSize}px`} />}
      </Section>
      <Section title="State">
        {['interactive', 'editable', 'scrollable'].map(k => (
          <PropRow key={k} k={k} v={String(!!el[k])} />
        ))}
      </Section>
    </>
  );
}

function FrameInspector({ f }: { f: FrameModel | undefined }) {
  if (!f) return null;
  return (
    <>
      <Section title={`Frame ${f.index}`}>
        <PropRow k="url" v={(f.url || '').substring(0, 40)} />
        <PropRow k="elements" v={String(f.elements.length)} />
        <PropRow k="interactive" v={String(f.elements.filter(e => e.interactive).length)} />
        <PropRow k="trigger" v={f.triggerType} />
      </Section>
      <div className="p-4 text-xs text-muted-foreground flex items-start gap-2">
        <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span>Hover a rect in the viewport to see that element's details here.</span>
      </div>
    </>
  );
}

function TransitionInspector({ t }: { t: TransitionModel | undefined }) {
  if (!t) return null;
  return (
    <>
      <Section title="Transition">
        <PropRow k="action" v={t.action} />
        <PropRow k="from → to" v={`Frame ${t.from} → ${t.to}`} />
        <PropRow k="cursor" v={`${t.cursor.x}, ${t.cursor.y}`} />
      </Section>
      {t.target && (
        <Section title="Target Element">
          <PropRow k="tag" v={`<${t.target.tag}>`} />
          <PropRow k="text" v={t.target.text} />
          {t.target.rect && <PropRow k="rect" v={`${t.target.rect.x},${t.target.rect.y} ${t.target.rect.w || t.target.rect.width}×${t.target.rect.h || t.target.rect.height}`} />}
        </Section>
      )}
      <div className="p-4 text-xs text-muted-foreground flex items-start gap-2">
        <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span>Showing before-frame. The amber cursor marks where the action happens.</span>
      </div>
    </>
  );
}

function NotesPanel({ frameIdx, frameStates, onUpdateNote, onSetStatus }: {
  frameIdx: number; frameStates: FrameState[];
  onUpdateNote: (i: number, note: string) => void;
  onSetStatus: (i: number, status: 'include' | 'key' | 'skip') => void;
}) {
  const fs = frameStates[frameIdx];
  if (!fs) return null;
  return (
    <div className="p-4 space-y-5">
      <div>
        <label className="block text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
          Annotation for Frame {frameIdx}
        </label>
        <textarea
          className="w-full min-h-[96px] rounded-lg border border-border bg-card text-foreground text-xs p-3 resize-y outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 placeholder:text-muted-foreground transition"
          placeholder="Describe what's happening, what to highlight in the demo…"
          value={fs.note}
          onChange={e => onUpdateNote(frameIdx, e.target.value)}
        />
      </div>
      <div>
        <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Frame Status</h3>
        <div className="grid grid-cols-3 gap-1.5">
          <button
            className={cn(
              'flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold border transition',
              fs.selected && !fs.keyframe
                ? 'bg-primary border-primary text-primary-foreground shadow-[0_0_0_1px_hsl(var(--primary)/0.25),0_4px_12px_hsl(var(--primary)/0.2)]'
                : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-border/80',
            )}
            onClick={() => onSetStatus(frameIdx, 'include')}
          >
            <Check className="h-3.5 w-3.5" />
            Include
          </button>
          <button
            className={cn(
              'flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold border transition',
              fs.keyframe
                ? 'bg-emerald-500 border-emerald-500 text-white shadow-[0_4px_12px_rgba(16,185,129,0.25)]'
                : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-border/80',
            )}
            onClick={() => onSetStatus(frameIdx, 'key')}
          >
            <Star className="h-3.5 w-3.5" fill={fs.keyframe ? 'currentColor' : 'none'} />
            Key
          </button>
          <button
            className={cn(
              'flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold border transition',
              fs.skipped
                ? 'bg-destructive/15 border-destructive/40 text-destructive'
                : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-border/80',
            )}
            onClick={() => onSetStatus(frameIdx, 'skip')}
          >
            <X className="h-3.5 w-3.5" />
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}
